import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  User,
  Phone,
  Mail,
  Calendar,
  Ear,
  Stethoscope,
  MessageCircle,
  Clock,
  FileText,
  ChevronRight,
  Send,
  Download,
  Mic,
  Video,
  Activity,
  Plus,
  ClipboardList,
  Edit
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const NOTE_TYPES = [
  { value: "clinical", label: "Clinical Observation", color: "bg-blue-100 text-blue-700" },
  { value: "diagnosis", label: "Diagnosis", color: "bg-purple-100 text-purple-700" },
  { value: "treatment_plan", label: "Treatment Plan", color: "bg-emerald-100 text-emerald-700" },
  { value: "follow_up", label: "Follow-up", color: "bg-amber-100 text-amber-700" },
  { value: "counseling", label: "Counseling", color: "bg-cyan-100 text-cyan-700" },
  { value: "referral", label: "Referral", color: "bg-red-100 text-red-700" },
  { value: "general", label: "General", color: "bg-slate-100 text-slate-700" }
];

export default function PatientDetailPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [doctorNotes, setDoctorNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("timeline");
  
  const [newNote, setNewNote] = useState({
    note_type: "clinical",
    title: "",
    content: "",
    is_private: false
  });

  useEffect(() => {
    fetchPatientTimeline();
    fetchDoctorNotes();
  }, [patientId]);

  const fetchPatientTimeline = async () => {
    try {
      const response = await apiClient.get(`/patients/${patientId}/timeline`);
      setPatient(response.data.patient);
      setTimeline(response.data.timeline);
    } catch (error) {
      console.error("Failed to fetch patient:", error);
      toast.error("Failed to load patient details");
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorNotes = async () => {
    try {
      const response = await apiClient.get(`/patients/${patientId}/doctor-notes`);
      setDoctorNotes(response.data);
    } catch (error) {
      console.error("Failed to fetch doctor notes:", error);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post(`/patients/${patientId}/doctor-notes`, {
        patient_id: patientId,
        ...newNote
      });
      setDoctorNotes([response.data, ...doctorNotes]);
      setNoteDialogOpen(false);
      setNewNote({ note_type: "clinical", title: "", content: "", is_private: false });
      toast.success("Doctor note added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add note");
    }
  };

  const getNoteTypeInfo = (type) => {
    return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[6];
  };

  const sendWhatsAppRecommendation = async (recommendationId) => {
    try {
      await apiClient.post("/whatsapp/send-recommendation", null, {
        params: { patient_id: patientId, recommendation_id: recommendationId }
      });
      toast.success("Recommendation sent via WhatsApp! (Mocked)");
    } catch (error) {
      toast.error("Failed to send WhatsApp message");
    }
  };

  const downloadReport = async (testId, type = "audiogram") => {
    try {
      const response = await apiClient.get(`/reports/${type}/${testId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${testId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const downloadNoahExport = async () => {
    try {
      const response = await apiClient.get(`/patients/${patientId}/export/noah`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `noah_export_${patient?.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("NOAH XML exported successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export NOAH data");
    }
  };

  const sendReportToPatient = async () => {
    try {
      await apiClient.post(`/patients/${patientId}/send-report`, null, {
        params: { report_type: "audiogram" }
      });
      toast.success("Report link sent via WhatsApp! (Mocked)");
    } catch (error) {
      toast.error("Failed to send report");
    }
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case "pta_test":
        return <Ear className="w-4 h-4 text-emerald-600" />;
      case "speech_test":
        return <FileText className="w-4 h-4 text-blue-600" />;
      case "recommendation":
        return <Stethoscope className="w-4 h-4 text-violet-600" />;
      case "appointment":
        return <Calendar className="w-4 h-4 text-amber-600" />;
      case "whatsapp":
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTimelineColor = (type) => {
    switch (type) {
      case "pta_test":
        return "border-emerald-500";
      case "speech_test":
        return "border-blue-500";
      case "recommendation":
        return "border-violet-500";
      case "appointment":
        return "border-amber-500";
      case "whatsapp":
        return "border-green-500";
      default:
        return "border-slate-300";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 space-y-6">
          <div className="skeleton h-8 w-48"></div>
          <div className="skeleton h-32 w-full rounded-xl"></div>
          <div className="skeleton h-64 w-full rounded-xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="patient-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/patients")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">{patient?.name}</h1>
            <p className="text-slate-500">{patient?.age} years • {patient?.gender}</p>
          </div>
        </div>

        {/* Patient Info Card */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium text-slate-900">{patient?.phone}</p>
                </div>
              </div>
              {patient?.email && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{patient?.email}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Registered</p>
                  <p className="font-medium text-slate-900">
                    {new Date(patient?.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            {patient?.medical_history && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Medical History</p>
                <p className="text-slate-700">{patient.medical_history}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={() => navigate(`/pta-test/${patientId}`)}
            data-testid="new-pta-test-btn"
          >
            <Ear className="w-4 h-4 mr-2" />
            New PTA Test
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate(`/speech-test/${patientId}`)}
          >
            <Mic className="w-4 h-4 mr-2" />
            Speech Test
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate(`/tympanometry/${patientId}`)}
            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
          >
            <Activity className="w-4 h-4 mr-2" />
            Tympanometry
          </Button>
          <Button variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Appointment
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate("/tele-audiology")}
          >
            <Video className="w-4 h-4 mr-2" />
            Tele-Consultation
          </Button>
          <Button 
            variant="outline" 
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            onClick={sendReportToPatient}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Send Report (WhatsApp)
          </Button>
          <Button 
            variant="outline"
            className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
            onClick={downloadNoahExport}
            data-testid="noah-export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export NOAH XML
          </Button>
          <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Doctor Note
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Doctor Note</DialogTitle>
                <DialogDescription>Record clinical observations, diagnosis, or treatment notes</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateNote} className="space-y-4 mt-4">
                <div>
                  <Label>Note Type</Label>
                  <Select value={newNote.note_type} onValueChange={(v) => setNewNote({...newNote, note_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex items-center gap-2">
                            <Badge className={t.color}>{t.label}</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title (Optional)</Label>
                  <Input
                    placeholder="Brief title for the note"
                    value={newNote.title}
                    onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Note Content *</Label>
                  <Textarea
                    placeholder="Enter your clinical observations, diagnosis, or notes..."
                    rows={4}
                    value={newNote.content}
                    onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="private"
                    checked={newNote.is_private}
                    onChange={(e) => setNewNote({...newNote, is_private: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="private" className="text-sm font-normal">Mark as private (only visible to you)</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={!newNote.content}>
                    Save Note
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for Timeline and Doctor Notes */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-80">
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="notes">Doctor Notes ({doctorNotes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-4">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-sky-600" />
                  Patient Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timeline.length > 0 ? (
                  <div className="relative pl-8">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                    <div className="space-y-6">
                      {timeline.map((item, idx) => (
                        <div key={idx} className="relative">
                          <div className={`absolute -left-5 w-4 h-4 rounded-full bg-white border-2 ${getTimelineColor(item.type)}`}></div>
                      <Card className="ml-4">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">{getTimelineIcon(item.type)}</div>
                              <div>
                                <p className="font-medium text-slate-900 capitalize">
                                  {item.type.replace("_", " ")}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {new Date(item.timestamp).toLocaleString()}
                                </p>
                                
                                {/* PTA Test Details */}
                                {item.type === "pta_test" && (
                                  <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-slate-500">Left Ear:</span>
                                        <span className="ml-2 font-medium">
                                          {item.data.pta_left?.toFixed(1) || "N/A"} dB 
                                          <Badge variant="outline" className="ml-2 text-xs">
                                            {item.data.severity_left || "N/A"}
                                          </Badge>
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-slate-500">Right Ear:</span>
                                        <span className="ml-2 font-medium">
                                          {item.data.pta_right?.toFixed(1) || "N/A"} dB
                                          <Badge variant="outline" className="ml-2 text-xs">
                                            {item.data.severity_right || "N/A"}
                                          </Badge>
                                        </span>
                                      </div>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="mt-3"
                                      onClick={() => downloadReport(item.data.id, "audiogram")}
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Download PDF Report
                                    </Button>
                                  </div>
                                )}

                                {/* Recommendation Details */}
                                {item.type === "recommendation" && (
                                  <div className="mt-2 space-y-2">
                                    {item.data.recommendations?.slice(0, 2).map((rec, i) => (
                                      <div key={i} className="p-3 bg-violet-50 rounded-lg text-sm">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-violet-900">
                                            {rec.name} - {rec.brand}
                                          </span>
                                          <Badge className="bg-violet-600">
                                            {rec.match_percentage}% Match
                                          </Badge>
                                        </div>
                                        <p className="text-violet-700 mt-1">
                                          ₹{rec.price?.toLocaleString()}
                                        </p>
                                      </div>
                                    ))}
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="bg-green-50 border-green-200 text-green-700"
                                      onClick={() => sendWhatsAppRecommendation(item.data.id)}
                                    >
                                      <Send className="w-3 h-3 mr-1" />
                                      Send via WhatsApp
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="ml-2"
                                      onClick={() => downloadReport(item.data.id, "recommendation")}
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Download PDF
                                    </Button>
                                  </div>
                                )}

                                {/* WhatsApp Message */}
                                {item.type === "whatsapp" && (
                                  <div className="mt-2 p-3 bg-green-50 rounded-lg text-sm">
                                    <p className="text-green-800">{item.data.content?.substring(0, 100)}...</p>
                                    <Badge variant="outline" className="mt-2 text-xs text-green-600">
                                      {item.data.direction === "outbound" ? "Sent" : "Received"}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No activity recorded yet</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate(`/pta-test/${patientId}`)}
                >
                  Start with a hearing test
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Doctor Notes Tab */}
          <TabsContent value="notes" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-600" />
                  Doctor Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {doctorNotes.length > 0 ? (
                  <div className="space-y-4">
                    {doctorNotes.map((note) => {
                      const typeInfo = getNoteTypeInfo(note.note_type);
                      return (
                        <div key={note.id} className="p-4 border rounded-lg hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                                {note.is_private && (
                                  <Badge variant="outline" className="text-xs">Private</Badge>
                                )}
                              </div>
                              {note.title && (
                                <h4 className="font-semibold text-slate-900 mb-1">{note.title}</h4>
                              )}
                              <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {note.created_by_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No doctor notes recorded</p>
                    <Button 
                      variant="link" 
                      className="mt-2"
                      onClick={() => setNoteDialogOpen(true)}
                    >
                      Add the first note
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
